import { Component, Input, OnInit, OnChanges, OnDestroy, SimpleChanges, inject, signal, computed, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { FormsModule } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, tap, finalize } from 'rxjs/operators';
import { ParkingRightService, ParkingRightDto, ParkingSpaceDto, TimeBasedParkingRightDto, DurationBasedParkingRightDto, ParkingRightTypeEnum, ListParkingRightsRequest, SortOrder, LicensePlateService, LicensePlateDto, ListLicensePlatesRequest, ParkingTypeEnum, ParkingTypeService, ParkingTypeDefnitionDto } from '../../../api/gen';
import { JjamesButtonComponent, JJamesBadgeComponent, JJamesModalComponent, Role } from 'shared-lib';
import { TranslatePipe } from '@ngx-translate/core';
import { ParkingRightAddComponent, ParkingRightAddDialogData } from '../parking-right-add/parking-right-add.component';
import { ParkingRightEditComponent, ParkingRightEditDialogData } from '../parking-right-edit/parking-right-edit.component';
import { ParkingRightImportComponent, ParkingRightImportDialogData } from '../parking-right-import/parking-right-import.component';
import { SiteMappingDialogComponent, SiteMappingDialogData } from '../parking-right-import/site-mapping/site-mapping-dialog.component';
import { PoolCapBackfillDialogComponent, PoolCapBackfillDialogData } from '../pool-cap-backfill/pool-cap-backfill-dialog.component';
import { ParkingTypeUnion, ParkingTypeOption } from '../parking-right.component';
import { RoleService } from '../../auth/role-service';

@Component({
  selector: 'app-parking-right-list',
  imports: [
    CommonModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    MatSnackBarModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatAutocompleteModule,
    FormsModule,
    JjamesButtonComponent,
    JJamesBadgeComponent,
    JJamesModalComponent,
    TranslatePipe
  ],
  templateUrl: './parking-right-list.component.html',
  styleUrl: './parking-right-list.component.scss'
})
export class ParkingRightListComponent implements OnInit, OnChanges, OnDestroy {
  private parkingRightService = inject(ParkingRightService);
  private licensePlateService = inject(LicensePlateService);
  private parkingTypeService = inject(ParkingTypeService);
  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);
  private roleService = inject(RoleService);

  @Input() parkingSpace: ParkingSpaceDto | null = null;
  @Input() allParkingSpaces: ParkingSpaceDto[] = [];
  @Input() allParkingTypes: ParkingTypeUnion[] = [];
  @Input() parkingTypeOptions: ParkingTypeOption[] = [];
  @Input() isLoadingTypes = false;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  displayedColumns = ['expand', 'name', 'parkingType', 'validity', 'licensePlates', 'zones', 'isActive', 'actions'];
  dataSource = new MatTableDataSource<ParkingRightDto>([]);
  isLoading = signal(false);
  parkingRights = signal<ParkingRightDto[]>([]);
  expandedElement: ParkingRightDto | null = null;

  pageSize = signal(10);
  pageIndex = signal(0);
  totalCount = signal(0);
  sortBy = signal<string | null>(null);
  sortOrder = signal<SortOrder>(SortOrder.Ascending);
  /** CSV import and old-DB sync mapping are Administrator-only — operator admins get "Add Right" only. */
  isImportAllowed = signal(false);
  isMappingAllowed = signal(false);

  // Delete confirmation modal
  deleteConfirmOpen = signal(false);
  rightToDelete = signal<ParkingRightDto | null>(null);

  // LP autocomplete
  filteredLpOptions = signal<LicensePlateDto[]>([]);
  isLoadingLps = signal(false);
  lpSearchTerm = signal('');
  selectedLicensePlateId = signal<string | null>(null);
  private lpSearch$ = new Subject<string>();
  private lpSearchSub!: Subscription;

  // Parking-type-definition filter (server-side via parkingTypeDefinitionId).
  selectedParkingTypeDefinitionId = signal<string | null>(null);
  parkingTypeDefinitions = signal<ParkingTypeDefnitionDto[]>([]);

  // Status quick-filter (client-side over the loaded page)
  selectedStatus = signal<'all' | 'active' | 'inactive'>('all');

  // Free-text search over name or comment (server-side).
  searchTerm = signal('');

  /** True when any non-default filter is applied — drives the "Clear" button. */
  hasActiveFilters = computed(() =>
    !!this.selectedLicensePlateId() || !!this.selectedParkingTypeDefinitionId() || this.selectedStatus() !== 'all' || !!this.searchTerm().trim()
  );

  ngOnInit(): void {
    this.checkToolbarAccess();
    this.setupLpSearch();
    this.loadLicensePlates();
    this.loadParkingTypeDefinitions();
    this.loadParkingRights();
  }

  ngOnDestroy(): void {
    this.lpSearchSub?.unsubscribe();
  }

  private setupLpSearch(): void {
    this.lpSearchSub = this.lpSearch$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      tap(() => this.isLoadingLps.set(true)),
      switchMap(term => {
        const request: ListLicensePlatesRequest = {
          pageSize: 50,
          pageIndex: 0,
          includeParkers: true,
          fullNumber: term && typeof term === 'string' ? term.toUpperCase() : null
        };
        return this.licensePlateService.operatorLicensePlateFilterPost(request).pipe(
          finalize(() => this.isLoadingLps.set(false))
        );
      })
    ).subscribe({
      next: (result) => {
        this.filteredLpOptions.set(result.items || []);
      },
      error: (err) => {
        console.error('Failed to search license plates', err);
        this.filteredLpOptions.set([]);
      }
    });
  }

  private async checkToolbarAccess(): Promise<void> {
    try {
      const isAdmin = await this.roleService.checkAccess(Role.Administrator);
      this.isImportAllowed.set(isAdmin);
      this.isMappingAllowed.set(isAdmin);
    } catch (error) {
      console.error('Error checking toolbar access:', error);
      this.isImportAllowed.set(false);
      this.isMappingAllowed.set(false);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['parkingSpace'] && !changes['parkingSpace'].firstChange) {
      this.pageIndex.set(0);
      this.loadParkingRights();
    }
  }

  ngAfterViewInit(): void {
  }

  loadParkingRights(): void {
    if (!this.parkingSpace?.id) return;

    this.isLoading.set(true);

    // Server-side filters: parkingTypeDefinitionId + searchTerm are part of the
    // regenerated ListParkingRightsRequest.
    const request: ListParkingRightsRequest = {
      allowedParkingSpaces: [this.parkingSpace.id],
      includeDetails: true,
      pageIndex: this.pageIndex(),
      pageSize: this.pageSize(),
      sortBy: this.sortBy(),
      sortOrder: this.sortOrder(),
      allowedLicensePlates: this.selectedLicensePlateId() ? [this.selectedLicensePlateId()!] : null,
      parkingTypeDefinitionId: this.selectedParkingTypeDefinitionId(),
      searchTerm: this.searchTerm().trim() || null
    };

    this.parkingRightService.parkingRightsFilterPost(request).subscribe({
      next: (result) => {
        const rights = result.items || [];
        this.parkingRights.set(rights);
        this.applyStatusFilter();
        this.totalCount.set(result.totalCount || 0);
        this.isLoading.set(false);
      },
      error: (error) => {
        console.error('Error loading parking rights:', error);
        this.snackBar.open('Failed to load parking rights', 'Close', { duration: 3000 });
        this.isLoading.set(false);
      }
    });
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex.set(event.pageIndex);
    this.pageSize.set(event.pageSize);
    this.loadParkingRights();
  }

  onSortChange(sort: Sort): void {
    if (!sort.active || !sort.direction) {
      this.sortBy.set(null);
      this.sortOrder.set(SortOrder.Ascending);
    } else {
      this.sortBy.set(sort.active);
      this.sortOrder.set(sort.direction === 'desc' ? SortOrder.Descending : SortOrder.Ascending);
    }
    this.pageIndex.set(0);
    this.loadParkingRights();
  }

  loadLicensePlates(): void {
    this.lpSearch$.next('');
  }

  onLpSearchChange(value: string | LicensePlateDto): void {
    if (typeof value === 'object' && value !== null) {
      this.lpSearchTerm.set(value.fullNumber || '');
      this.selectedLicensePlateId.set(value.id || null);
      this.lpSearch$.next(value.fullNumber || '');
      return;
    }
    const strVal = typeof value === 'string' ? value : '';
    this.lpSearchTerm.set(strVal);
    if (!strVal) {
      this.selectedLicensePlateId.set(null);
    }
    this.lpSearch$.next(strVal);
  }

  onLpSelected(lp: LicensePlateDto): void {
    if (!lp) return;
    this.selectedLicensePlateId.set(lp.id || null);
    this.lpSearchTerm.set(lp.fullNumber || '');
  }

  displayLp = (lp: LicensePlateDto | string): string => {
    if (!lp) return '';
    if (typeof lp === 'string') return lp;
    return lp.fullNumber || '';
  };

  applySearchFilters(): void {
    this.pageIndex.set(0);
    this.loadParkingRights();
  }

  /** Server-side parking-type-definition filter change — reloads from the first page. */
  onParkingTypeFilterChange(definitionId: string | null): void {
    this.selectedParkingTypeDefinitionId.set(definitionId);
    this.pageIndex.set(0);
    this.loadParkingRights();
  }

  /** Status quick-filter change — refines the currently loaded page client-side. */
  onStatusFilterChange(status: 'all' | 'active' | 'inactive'): void {
    this.selectedStatus.set(status);
    this.applyStatusFilter();
  }

  clearFilters(): void {
    this.selectedLicensePlateId.set(null);
    this.lpSearchTerm.set('');
    this.selectedParkingTypeDefinitionId.set(null);
    this.selectedStatus.set('all');
    this.searchTerm.set('');
    this.pageIndex.set(0);
    this.loadParkingRights();
  }

  /** Loads the parking-type definitions that drive the filter dropdown. */
  private loadParkingTypeDefinitions(): void {
    this.parkingTypeService.parkingTypesGet().subscribe({
      next: (definitions) => {
        this.parkingTypeDefinitions.set(
          (definitions || [])
            .filter(d => d.id && d.name)
            .sort((a, b) => a.name.localeCompare(b.name))
        );
      },
      error: (err) => {
        console.error('Failed to load parking type definitions', err);
      }
    });
  }

  /** Applies the active/inactive quick-filter to the loaded page rows. */
  private applyStatusFilter(): void {
    const status = this.selectedStatus();
    const rows = this.parkingRights();
    this.dataSource.data = status === 'all'
      ? rows
      : rows.filter(r => (status === 'active' ? r.isActive : !r.isActive));
  }

  openAddDialog(): void {
    const dialogRef = this.dialog.open(ParkingRightAddComponent, {
      width: '95vw',
      maxWidth: '600px',
      data: { parkingSpace: this.parkingSpace } as ParkingRightAddDialogData
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.loadParkingRights();
      }
    });
  }

  openEditDialog(parkingRight: ParkingRightDto): void {
    const dialogRef = this.dialog.open(ParkingRightEditComponent, {
      width: '95vw',
      maxWidth: '600px',
      data: { parkingRight, parkingSpace: this.parkingSpace } as ParkingRightEditDialogData
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.loadParkingRights();
      }
    });
  }

  openImportDialog(): void {
    if (!this.isImportAllowed()) {
      this.snackBar.open('You are not allowed to access Parking Right import.', 'Close', { duration: 4000 });
      return;
    }

    const dialogRef = this.dialog.open(ParkingRightImportComponent, {
      width: '1100px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: {
        parkingSpace: this.parkingSpace,
        allParkingTypes: this.allParkingTypes,
        parkingTypeOptions: this.parkingTypeOptions
      } as ParkingRightImportDialogData
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.loadParkingRights();
      }
    });
  }

  /**
   * Old-DB site mappings are global configuration for the migration sync, not per-right data, so
   * closing this dialog does not reload the list — nothing here changes until a sync runs.
   */
  openMappingDialog(): void {
    if (!this.isMappingAllowed() || !this.parkingSpace) {
      return;
    }

    this.dialog.open(SiteMappingDialogComponent, {
      width: '1100px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: {
        parkingSpace: this.parkingSpace,
        allParkingSpaces: this.allParkingSpaces
      } as SiteMappingDialogData
    });
  }

  /** Administrator-only, like the mapping dialog: the backend endpoint is Administrator-only. */
  openPoolCapBackfillDialog(): void {
    if (!this.isMappingAllowed() || !this.parkingSpace) {
      return;
    }

    const dialogRef = this.dialog.open(PoolCapBackfillDialogComponent, {
      width: '640px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: { parkingSpace: this.parkingSpace } as PoolCapBackfillDialogData
    });

    dialogRef.afterClosed().subscribe((applied) => {
      if (applied) {
        this.loadParkingRights();
      }
    });
  }



  deleteParkingRight(parkingRight: ParkingRightDto): void {
    if (!parkingRight.id) return;
    this.rightToDelete.set(parkingRight);
    this.deleteConfirmOpen.set(true);
  }

  confirmDelete(): void {
    const right = this.rightToDelete();
    if (!right?.id) return;
    this.deleteConfirmOpen.set(false);
    this.parkingRightService.parkingRightsParkingRightIdDelete(right.id).subscribe({
      next: () => {
        this.snackBar.open('Parking right deleted', 'Close', { duration: 3000 });
        this.rightToDelete.set(null);
        this.loadParkingRights();
      },
      error: () => {
        this.snackBar.open('Failed to delete parking right', 'Close', { duration: 3000 });
        this.rightToDelete.set(null);
      }
    });
  }

  cancelDelete(): void {
    this.deleteConfirmOpen.set(false);
    this.rightToDelete.set(null);
  }

  getParkingTypeName(right: ParkingRightDto): string {
    return right.parkingType?.type?.name || 'N/A';
  }

  isPoolParking(right: ParkingRightDto): boolean {
    return right.parkingType?.type?.type === ParkingTypeEnum.PoolParking;
  }

  isPoolParkingSpace(): boolean {
    const options = this.parkingTypeOptions;
    return options.length > 0 && options.every(o => o.category === 'Pool Parking');
  }

  getLicensePlateNumbers(right: ParkingRightDto): Array<string> {
    return right.allowedLicensePlates?.map(plate => plate.licensePlateDto?.fullNumber || 'Unknown') || [];
  }

  getZones(right: ParkingRightDto): Array<string> {
    var zones = this.parkingSpace?.parkingZones?.filter(zone => right.allowedParkingZones?.some(allowedZone => allowedZone.parkingZoneId === zone.id));
    return zones?.map(zone => zone.name || 'Unknown') || [];
  }

  getValidTo(right: ParkingRightDto): string | null {
    const typeDto = right.parkingRightType as TimeBasedParkingRightDto | DurationBasedParkingRightDto;
    return typeDto?.validTo || null;
  }

  getValidFrom(right: ParkingRightDto): string | null {
    const typeDto = right.parkingRightType as TimeBasedParkingRightDto | DurationBasedParkingRightDto;
    return typeDto?.validFrom || null;
  }

  isTimeBased(right: ParkingRightDto): boolean {
    return right.parkingRightType?.type === ParkingRightTypeEnum.TimeBased;
  }

  isDurationBased(right: ParkingRightDto): boolean {
    return right.parkingRightType?.type === ParkingRightTypeEnum.DurationBased;
  }

  getTimeBasedInfo(right: ParkingRightDto): string {
    const typeDto = right.parkingRightType as TimeBasedParkingRightDto;
    if (!typeDto) return 'N/A';

    const parts: string[] = [];

    if (typeDto.isAllDay) {
      parts.push('All Day');
    } else if (typeDto.startTime && typeDto.endTime) {
      parts.push(`${typeDto.startTime} - ${typeDto.endTime}`);
    }

    if (typeDto.validDays && typeDto.validDays.length > 0) {
      const dayAbbrev = typeDto.validDays.map(d => d.substring(0, 3)).join(', ');
      parts.push(dayAbbrev);
    }

    if (typeDto.isRecurring) {
      parts.push('Recurring');
    }

    return parts.length > 0 ? parts.join(' | ') : 'N/A';
  }

  getDurationBasedInfo(right: ParkingRightDto): string {
    const typeDto = right.parkingRightType as DurationBasedParkingRightDto;
    if (!typeDto) return 'N/A';

    const parts: string[] = [];

    if (typeDto.durationInMinutes) {
      const hours = Math.floor(typeDto.durationInMinutes / 60);
      const mins = typeDto.durationInMinutes % 60;
      if (hours > 0 && mins > 0) {
        parts.push(`${hours}h ${mins}m`);
      } else if (hours > 0) {
        parts.push(`${hours}h`);
      } else {
        parts.push(`${mins}m`);
      }
    }

    if (typeDto.isRenewable) {
      parts.push(typeDto.maxRenewals ? `Renewable (max ${typeDto.maxRenewals})` : 'Renewable');
    }

    return parts.length > 0 ? parts.join(' | ') : 'N/A';
  }

  // Expandable row methods
  toggleRow(element: ParkingRightDto): void {
    this.expandedElement = this.expandedElement === element ? null : element;
  }

  // Time-based helper methods for expanded view
  getTimeRange(right: ParkingRightDto): string {
    const typeDto = right.parkingRightType as TimeBasedParkingRightDto;
    if (!typeDto) return 'N/A';

    if (typeDto.isAllDay) {
      return 'All Day (24 hours)';
    }

    if (typeDto.startTime && typeDto.endTime) {
      return `${typeDto.startTime} - ${typeDto.endTime}`;
    }

    return 'Not specified';
  }

  getValidDays(right: ParkingRightDto): string {
    const typeDto = right.parkingRightType as TimeBasedParkingRightDto;
    if (!typeDto?.validDays || typeDto.validDays.length === 0) {
      return 'All days';
    }

    if (typeDto.validDays.length === 7) {
      return 'Every day';
    }

    // Check for weekdays only
    const weekdays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
    const weekends = ['Saturday', 'Sunday'];

    if (typeDto.validDays.length === 5 && weekdays.every(d => typeDto.validDays?.includes(d as any))) {
      return 'Weekdays only';
    }

    if (typeDto.validDays.length === 2 && weekends.every(d => typeDto.validDays?.includes(d as any))) {
      return 'Weekends only';
    }

    return typeDto.validDays.map(d => d.substring(0, 3)).join(', ');
  }

  isRecurring(right: ParkingRightDto): boolean {
    const typeDto = right.parkingRightType as TimeBasedParkingRightDto;
    return typeDto?.isRecurring || false;
  }

  // Duration-based helper methods for expanded view
  getDurationText(right: ParkingRightDto): string {
    const typeDto = right.parkingRightType as DurationBasedParkingRightDto;
    if (!typeDto?.durationInMinutes) return 'N/A';

    const hours = Math.floor(typeDto.durationInMinutes / 60);
    const mins = typeDto.durationInMinutes % 60;

    if (hours > 0 && mins > 0) {
      return `${hours} hour${hours > 1 ? 's' : ''} ${mins} minute${mins > 1 ? 's' : ''}`;
    } else if (hours > 0) {
      return `${hours} hour${hours > 1 ? 's' : ''}`;
    } else {
      return `${mins} minute${mins > 1 ? 's' : ''}`;
    }
  }

  isRenewable(right: ParkingRightDto): boolean {
    const typeDto = right.parkingRightType as DurationBasedParkingRightDto;
    return typeDto?.isRenewable || false;
  }

  getMaxRenewals(right: ParkingRightDto): string {
    const typeDto = right.parkingRightType as DurationBasedParkingRightDto;
    return typeDto?.maxRenewals ? `${typeDto.maxRenewals}` : 'Unlimited';
  }

  /** Skeleton placeholder rows rendered while the rights payload is loading. */
  readonly skeletonRows = Array.from({ length: 5 });
}
